import {useEffect, useMemo, useRef, useState} from 'react';
import Axios from 'axios';

import type {
	SearchResult,
	SubLabelSearchResult,
} from '@deezer/next/services/api/Starmania/requests/search/types';
import type {SubLabel} from '@deezer/next/contexts/main/types';
import StarmaniaApi from '@deezer/next/services/api/Starmania';

import {uniqid} from '@deezer/next/shared/utils/id';
import {ContentType} from '@deezer/next/services/types/contentType';

import {useDebouncer} from '@deezer/next/shared/hooks/useDebouncer';

type SearchFunction = {
	(searchQuery: string, params?: object): Promise<void> | undefined;

	cancel: () => void;
	flush: () => Promise<void> | undefined;
};

const mapSubLabelToSearchResult = (
	subLabel: SubLabel,
): SubLabelSearchResult => ({
	id: subLabel.labelId,
	name: subLabel.labelName,
	providerId: subLabel.providerId,
	providerName: subLabel.providerName,
});

export const useSearch = <T extends ContentType>(type: T) => {
	const [isSearching, setIsSearching] = useState(false);
	const [results, setResults] = useState<SearchResult<T>>();
	const [error, setError] = useState<any>();

	const requestIdsRef = useRef<string[]>([]);

	const {schedule, cancel, flush} = useDebouncer<Promise<void>>(200);

	const search = useMemo<SearchFunction>(() => {
		const debouncedSearch = (
			searchQuery: string,
			params?: object,
		): Promise<void> | undefined =>
			schedule(async () => {
				const previousRequestIds = [...requestIdsRef.current];

				const newRequestId = uniqid();

				requestIdsRef.current.push(newRequestId);

				setIsSearching(true);

				try {
					previousRequestIds.forEach(rId => {
						StarmaniaApi.cancel(rId);
					});

					let results: SearchResult<T>;

					if (type === ContentType.SubLabel) {
						const response = await StarmaniaApi.users.getSubLabels({
							includeDisabledProviders: false,
						});

						results = response.data.map(
							mapSubLabelToSearchResult,
						) as SearchResult<T>;
					} else if (type === ContentType.Release) {
						const response =
							await StarmaniaApi.pitches.getPitchReleases(searchQuery);

						results = response.data as SearchResult<T>;
					} else {
						const response = await StarmaniaApi.search.searchItem(
							type,
							searchQuery,
							newRequestId,
							params,
						);

						results = response.data as SearchResult<T>;
					}

					setResults(results);
					setError(undefined);
					setIsSearching(false);
				} catch (error) {
					if (!Axios.isCancel(error)) {
						setResults(undefined);
						setError(error);
						setIsSearching(false);
					}
				} finally {
					// Clear out the current request id no matter if
					// it's successful or cancelled.
					requestIdsRef.current = requestIdsRef.current.filter(
						requestId => requestId !== newRequestId,
					);
				}
			});
		debouncedSearch.cancel = cancel;
		debouncedSearch.flush = flush;

		return debouncedSearch;
	}, [cancel, flush, schedule, type]);

	useEffect(() => {
		return () => {
			search.cancel();
		};
	}, [search]);

	return {
		isSearching,
		results,
		error,
		setResults,
		search,
	};
};

export default useSearch;
